import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ClientService } from '../../../services/client.service';
import { BaseComponent, customMetricEvents, CustomMetricsComponent, featureCustomMetric, FeatureFlagService, Page, PusherService, UserService } from '@rubii/frontend-common';
import { CustomMetric, CustomMetricStatus } from '@rubii/sdk-web-custom-metric/src/types';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WsEvent } from '@rubii/frontend-common/lib/models/events/ws-event.class';
import { Metric } from '@rubii/sdk-cdp-report/src/types';

@Component({
    selector: 'app-custom-metric-list',
    templateUrl: './custom-metric-list.component.html',
    styleUrls: ['./custom-metric-list.component.scss'],
})
export class CustomMetricListComponent extends BaseComponent {

    private allCustomMetrics: CustomMetric[] = [];

    public customMetrics: CustomMetric[] = [];
    metrics: Metric[] = [];
    public timezone = '';
    keyword = '';

    format: string = '';

    CustomMetricStatus = CustomMetricStatus;

    constructor(
        private client: ClientService,
        public user: UserService,
        private dialog: MatDialog,
        private snack: MatSnackBar,
        private pusher: PusherService,
        private feature: FeatureFlagService,
    ) {
        super();
        this.page = new Page(1, 15, 0);
        this.format = this.user.user.tenant.dateFormat.replace('YYYY', 'yyyy').replace('DD', 'dd') + ' HH:mm:ss OOOO';
        this.timezone = this.user.user.tenant.timezone;
        this.pusher.bind('custom-metric-list', 'custom-metric-updated', this.customMetricUpdated.bind(this));
    }

    async customMetricUpdated(event: WsEvent) {
        this.allCustomMetrics = (await this.client.customMetric.list()).sort((a, b) => b.lastModifiedTime - a.lastModifiedTime);
        this.paginate();
    }

    async ngOnInit() {
        this.init();
        this.metrics = await this.client.widget.getAllMetrics();
    }

    async init() {
        this.page.pageId = 1;
        await this.customMetricUpdated(null);
    }

    paginate() {
        const filteredCMs = this.allCustomMetrics.filter(cm => !this.keyword || (cm.name + ' ' + cm.displayName).toLowerCase().includes(this.keyword.toLowerCase()));
        this.page.total = filteredCMs.length;
        if (filteredCMs.length === 0) {
            this.page.pageId = 1;
            this.customMetrics = [];
        } else {
            this.page.pageId = Math.min(this.page.pageId, Math.ceil(filteredCMs.length / this.page.pageSize));
            this.customMetrics = filteredCMs.slice((this.page.pageId - 1) * this.page.pageSize, this.page.pageId * this.page.pageSize);
        }
    }

    async archive(customMetric: CustomMetric) {
        try {
            await this.client.customMetric.archive(customMetric.id);
            this.allCustomMetrics.find(cm => cm.id === customMetric.id).status = CustomMetricStatus.Archived;
            this.paginate();   
            await this.feature.trigger(featureCustomMetric, customMetricEvents.ArchivedFromHomePage, { customMetricId: customMetric.id });
        } catch (error) {
            this.openSnackBarWarning(this.snack, error.message, 'close');
        }
    }

    pageChanged(event: PageEvent) {
        this.page.pageId = event.pageIndex + 1;
        this.page.pageSize = event.pageSize;
        this.paginate();
    }

    clone(customMetric: CustomMetric) {
        const cloned = _.cloneDeep(customMetric);
        cloned.id = null;
        cloned.name = 'Copy of ' + cloned.name;
        cloned.displayName = 'Copy of ' + cloned.displayName;
        const ref = this.dialog.open(CustomMetricsComponent, {
            data: {
                inputCustomMetric: cloned,
                metrics: this.metrics,
            },
            height: 'calc(100vh - 100px)',
            width: '800px',
        });
        ref.afterClosed().subscribe(async (result) => {
            await this.init();
            await this.feature.trigger(featureCustomMetric, customMetricEvents.ClonedFromHomePage, { customMetricId: result.id });
        });
    }

    edit(customMetric: CustomMetric) {
        console.log(customMetric);
        const ref = this.dialog.open(CustomMetricsComponent, {
            data: {
                inputCustomMetric: customMetric,
                metrics: this.metrics,
            },
            // height: 'calc(100vh - 100px)',
            width: '800px',
        });
        ref.afterClosed().subscribe(async (result) => {
            await this.feature.trigger(featureCustomMetric, customMetricEvents.EditFromHomePage, { customMetricId: result.id });
        });
    }

    create() {
        const ref = this.dialog.open(CustomMetricsComponent, {
            data: {
                metrics: this.metrics,
            },
            // height: 'calc(100vh - 100px)',
            width: '800px',
        });
        ref.afterClosed().subscribe(async (result) => {
            await this.init();
            await this.feature.trigger(featureCustomMetric, customMetricEvents.CreatedFromHomePage, { customMetricId: result.id });
        });
    }

    search(keyword: string) {
        this.keyword = keyword;
        this.paginate();
    }

    async enable(customMetric: CustomMetric) {
        await this.client.customMetric.enable(customMetric.id);
        this.allCustomMetrics.find(cm => cm.id === customMetric.id).status = CustomMetricStatus.Active;
        this.paginate();
        await this.feature.trigger(featureCustomMetric, customMetricEvents.EnabledFromHomePage, { customMetricId: customMetric.id });
    }
}
